import { useRef, useState } from "react";
import { History, PackagePlus, Send } from "lucide-react";
import { useUpdateBoxTransaction } from "@/features/checkin/hooks";
import {
  buildBoxMovementPayload,
  buildSupplierBoxMovementDisplay,
  formatLatestBoxMovement,
  parseBoxMovementInput,
  type BoxMovementInputValue,
  type BoxMovementStatus,
} from "@/features/checkin/boxMovementDisplay";
import type { SupplierBoxSummary } from "@/features/checkin/types";
import { getCheckinActionError } from "@/features/checkin/itemUi";
import { BoxMovementHistoryDrawer } from "./BoxMovementHistoryDrawer";

export function SupplierBoxCard({
  supplier,
  checkinId,
  readOnly,
  closed,
  canViewHistory,
  stale = false,
  onError,
}: {
  supplier: SupplierBoxSummary;
  checkinId: string;
  readOnly: boolean;
  closed: boolean;
  canViewHistory: boolean;
  stale?: boolean;
  onError: (message: string) => void;
}) {
  const [received, setReceived] = useState<BoxMovementInputValue>("");
  const [sent, setSent] = useState<BoxMovementInputValue>("");
  const [status, setStatus] = useState<BoxMovementStatus>("idle");
  const pendingLock = useRef(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const update = useUpdateBoxTransaction(checkinId);
  const display = buildSupplierBoxMovementDisplay({
    addReceived: received,
    addSent: sent,
    boxBalance: supplier.box_balance,
    readOnly: readOnly || stale,
    status,
  });

  async function move(action: "receive" | "send") {
    const quantity =
      action === "receive"
        ? display.currentAddReceived
        : display.currentAddSent;
    if (!quantity || readOnly || stale || display.isBusy || pendingLock.current)
      return;
    pendingLock.current = true;
    setStatus("loading");
    try {
      await update.mutateAsync({
        supplierID: supplier.supplier_id,
        ...buildBoxMovementPayload(action, quantity),
      });
      if (action === "receive") setReceived("");
      else setSent("");
    } catch (error) {
      onError(getCheckinActionError(error));
    } finally {
      pendingLock.current = false;
      setStatus("idle");
    }
  }

  return (
    <article className="supplier-box-card">
      <header className="supplier-box-head">
        <div>
          <strong>{supplier.supplier_name}</strong>
          <span className="cell-sub">Movimentação nesta carga</span>
        </div>
        <div className="supplier-box-head-side">
          <div className="balance">
            <span>Saldo global</span>
            <strong>
              {supplier.box_balance > 0
                ? `+${supplier.box_balance}`
                : supplier.box_balance}{" "}
              caixas
            </strong>
          </div>
          {readOnly ? (
            <span className="box-readonly">Somente leitura</span>
          ) : null}
        </div>
      </header>
      <div className="supplier-box-facts">
        <div>
          <span>Recebidas nesta carga</span>
          <strong>{supplier.boxes_received} caixas</strong>
        </div>
        <div>
          <span>Enviadas nesta carga</span>
          <strong>{supplier.boxes_sent} caixas</strong>
        </div>
      </div>
      <div
        className={`box-movement${readOnly ? " box-movement--readonly" : ""}`}
      >
        {supplier.latest_movement ? (
          <div className="undo-control">
            <div className="undo-info">
              <strong>
                {formatLatestBoxMovement(supplier.latest_movement)}
              </strong>
            </div>
          </div>
        ) : null}
        {!readOnly ? (
          <>
            <div className="box-quick-action receive">
              <span className="sr-only">Caixas recebidas agora</span>
              <input
                aria-label="Caixas recebidas agora"
                type="number"
                min={0}
                step={1}
                placeholder="0"
                value={received}
                disabled={stale}
                onChange={(event) =>
                  setReceived(parseBoxMovementInput(event.target.value))
                }
              />
              <button
                type="button"
                onClick={() => void move("receive")}
                disabled={
                  stale || !display.currentAddReceived || display.isBusy
                }
              >
                <PackagePlus size={19} /> Receber
              </button>
            </div>
            <div className="box-quick-action send">
              <span className="sr-only">Caixas enviadas agora</span>
              <input
                aria-label="Caixas enviadas agora"
                type="number"
                min={0}
                step={1}
                placeholder="0"
                value={sent}
                disabled={stale}
                onChange={(event) =>
                  setSent(parseBoxMovementInput(event.target.value))
                }
              />
              <button
                type="button"
                onClick={() => void move("send")}
                disabled={stale || !display.currentAddSent || display.isBusy}
              >
                <Send size={19} /> Enviar
              </button>
            </div>
          </>
        ) : null}
        {canViewHistory ? (
          <button
            className="box-history-trigger"
            type="button"
            onClick={() => setHistoryOpen(true)}
          >
            <History size={16} /> Ver histórico
          </button>
        ) : null}
      </div>
      {canViewHistory ? (
        <BoxMovementHistoryDrawer
          open={historyOpen}
          checkinId={checkinId}
          supplier={supplier}
          readOnly={readOnly}
          closed={closed}
          stale={stale}
          onError={onError}
          onClose={() => setHistoryOpen(false)}
        />
      ) : null}
    </article>
  );
}
